/**
 * Payroll workbook hook — generate/review/approve/export lifecycle.
 *
 * Endpoints wrapped:
 *   POST /api/finance/payroll/generate              → generate workbook (also /compute fallback)
 *   GET  /api/finance/payroll/run/:id               → load run + workbook + adjustments
 *   POST /api/finance/payroll/run/:id/adjustment    → classified correction (run_only | forward_going | effective_dated)
 *   GET  /api/finance/payroll/run/:id/adjustments   → list this run's adjustments
 *   PATCH /api/finance/payroll/run/:id              → status transitions (draft → reviewed → approved → exported)
 *   GET  /api/finance/audit/pay-changes?employee_id → "who approved X at Y/day"
 */

import { useCallback, useState } from 'react';
import { apiFetch, apiWrite } from '../../lib/api-client.js';

export type PayrollRunStatus = 'draft' | 'reviewed' | 'approved' | 'exported';

export interface PayrollWorkbookRow {
  detail_row_id: string | null;
  employee_id: string | null;
  first_name: string;
  last_name: string;
  driver_id: string | null;
  terminal: string | null;
  rate: number | null;
  rate_type: string | null;
  days: number | null;
  base_pay: number | null;
  per_diem_rate: number | null;
  per_diem_pay: number | null;
  stop_bonus_eligible_stops: number | null;
  stop_bonus_amount: number | null;
  scorecard_tier: string | null;
  total_pay: number | null;
  work_dates?: string[];
  missing_fields: string[];
  blocking_reason: string | null;
}

export interface PayrollAdjustment {
  id: string;
  payroll_run_id: string;
  detail_row_id: string | null;
  employee_id: string | null;
  field_name: string;
  original_value: string | null;
  corrected_value: string;
  classification: 'run_only' | 'forward_going' | 'effective_dated';
  effective_date: string | null;
  reason: string | null;
  confirmation_code_entered: string | null;
  approved_by_email: string;
  approved_at: string;
  linked_profile_id: string | null;
  linked_edit_log_id: string | null;
}

export interface PayrollRun {
  id: string;
  week_ending: string;
  status: PayrollRunStatus;
  generated_by_email: string | null;
  generated_at: string | null;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  approved_by_email: string | null;
  approved_at: string | null;
  exported_at: string | null;
  driver_count: number | null;
  total_payroll: number | null;
  notes: string | null;
}

interface GenerateResponse {
  payroll_run_id: string;
  week_ending: string;
  status: PayrollRunStatus;
  drivers: number;
  blocked_rows: number;
  total_payroll: number;
  unmatched_drivers: string[];
  workbook: PayrollWorkbookRow[];
  error?: string;
}

interface RunsListResponse {
  runs: PayrollRun[];
  error?: string;
}

export async function fetchPayrollRuns() {
  return apiFetch<RunsListResponse>(`/api/finance/payroll/runs`);
}

interface RunDetailResponse {
  run: PayrollRun;
  details: PayrollWorkbookRow[];
  adjustments: PayrollAdjustment[];
  error?: string;
}

interface AdjustmentCreateBody {
  detail_row_id: string;
  employee_id: string | null;
  field_name: string;
  original_value: string | number | null;
  corrected_value: string | number;
  classification: 'run_only' | 'forward_going' | 'effective_dated';
  effective_date?: string;
  reason?: string;
  confirmation_code?: string;
}

export function usePayrollWorkbook() {
  const [run, setRun] = useState<PayrollRun | null>(null);
  const [rows, setRows] = useState<PayrollWorkbookRow[]>([]);
  const [adjustments, setAdjustments] = useState<PayrollAdjustment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async (weekEnding: string, opts: { force?: boolean; unlockCode?: string } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { week_ending: weekEnding };
      if (opts.force) body.force = true;
      if (opts.unlockCode) body.unlock_code = opts.unlockCode;
      const { ok, data } = await apiWrite<GenerateResponse>('/api/finance/payroll/generate', 'POST', body);
      if (!ok || data.error) {
        setError(data.error || 'Generate failed');
        return null;
      }
      // Hydrate workbook state
      setRun({
        id: data.payroll_run_id,
        week_ending: data.week_ending,
        status: data.status,
        generated_by_email: null, generated_at: null,
        reviewed_by_email: null, reviewed_at: null,
        approved_by_email: null, approved_at: null,
        exported_at: null,
        driver_count: data.drivers,
        total_payroll: data.total_payroll,
        notes: null,
      });
      setRows(data.workbook || []);
      setAdjustments([]);
      return data;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRun = useCallback(async (runId: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await apiFetch<RunDetailResponse>(`/api/finance/payroll/run/${runId}`);
      if (resp?.error) { setError(resp.error); return; }
      setRun(resp.run);
      setRows(resp.details || []);
      setAdjustments(resp.adjustments || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const postAdjustment = useCallback(async (runId: string, body: AdjustmentCreateBody) => {
    const { ok, data } = await apiWrite<{ adjustment: PayrollAdjustment; applied_to_run: boolean; error?: string }>(
      `/api/finance/payroll/run/${runId}/adjustment`, 'POST', body
    );
    if (!ok || data.error) {
      return { ok: false, error: data.error || 'Adjustment failed' };
    }
    // Refresh from server so numbers are in sync
    await loadRun(runId);
    return { ok: true, adjustment: data.adjustment, appliedToRun: data.applied_to_run };
  }, [loadRun]);

  const transitionStatus = useCallback(async (runId: string, nextStatus: PayrollRunStatus) => {
    const { ok, data } = await apiWrite<{ run: PayrollRun; error?: string }>(
      `/api/finance/payroll/run/${runId}`, 'PATCH', { status: nextStatus }
    );
    if (!ok || data.error) {
      return { ok: false, error: data.error || 'Status transition failed' };
    }
    setRun(data.run);
    return { ok: true, run: data.run };
  }, []);

  return {
    run, rows, adjustments, loading, error,
    generate, loadRun, postAdjustment, transitionStatus,
    setRun, setRows, setAdjustments,
  };
}

// ---------------------------------------------------------------------------
// Audit lookup — "who approved Jimmy Jack at 235/day?"
// ---------------------------------------------------------------------------

export interface PayChangeAuditRow {
  adjustment_id: string;
  payroll_run_id: string;
  week_ending: string | null;
  run_status: string | null;
  employee_id: string;
  first_name: string | null;
  last_name: string | null;
  driver_id: string | null;
  field_name: string;
  original_value: string | null;
  corrected_value: string;
  classification: string;
  effective_date: string | null;
  reason: string | null;
  confirmed: boolean;
  approved_by_email: string;
  approved_at: string;
}

export async function fetchPayChangeAudit(employeeId: string) {
  return apiFetch<{ employee_id: string; adjustments: PayChangeAuditRow[]; error?: string }>(
    `/api/finance/audit/pay-changes?employee_id=${encodeURIComponent(employeeId)}`
  );
}
